import styled from '@emotion/styled';
import React from 'react';
import { useTranslation } from 'react-i18next';
import infoIcon from 'src/public/img/information_icon_light.svg';
import { color, font } from 'src/shared/styles/variables';
import { IntakeFormTestID } from '../models/intakeForm';

interface WrapperProps {
    headSpace?: number;
}

const Wrapper = styled('div')<WrapperProps>(({ headSpace }) => ({
    display: 'flex',
    width: 540,
    justifyContent: 'flex-start',
    marginTop: Number.isFinite(headSpace) ? headSpace : 124,
}));

const CoachAvatarImg = styled('img')({
    width: 112,
    height: 112,
    borderRadius: 56,
});

const CoachAvatarPlaceholder = styled('div')({
    width: 112,
    height: 112,
    borderRadius: '100%',
    backgroundColor: color.cornflowerBlue,
    position: 'relative',
});

const CoachAvatarInitials = styled('div')(
    {
        top: '50%',
        transform: 'translateY(-50%)',
        textAlign: 'center',
        width: '100%',
        color: color.white,
        position: 'absolute',
        paddingTop: 6,
    },
    font.heavy(font.size.headerBigger),
);

const CoachNameWrapper = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
});

const CoachName = styled('h2')(
    {
        paddingLeft: 24,
        color: color.ebonyClay,
        display: 'flex',
        marginBottom: 0,
    },
    font.heavy(font.size.largeExtended),
);

const CoachCredentials = styled('h3')(
    {
        lineHeight: '28px',
        paddingLeft: 24,
        color: color.blueBayoux,
    },
    font.medium(font.size.mediumExtended),
);

interface Props {
    header?: number;
    coachAvatar: string;
    coachFirstName: string;
    coachLastName: string;
    coachCredentials: string;
    withInfo?: boolean;
    onInfoClick?: () => void;
}

const InfoButtonSpan = styled('div')({
    width: 22,
    height: 21,
    backgroundImage: `url(${infoIcon})`,
    cursor: 'pointer',
    marginLeft: 8,
    marginTop: 2,
});

export const CoachIntroWrapper: React.FC<Props> = ({
    header,
    coachAvatar,
    coachFirstName,
    coachLastName,
    coachCredentials,
    withInfo,
    onInfoClick = () => null,
}) => {
    const { t } = useTranslation();

    return (
        <Wrapper headSpace={header}>
            {coachAvatar ? (
                <CoachAvatarImg
                    src={coachAvatar}
                    alt={t('intakeForms:coach_intro.coach_image_alt_text', {
                        coachFirstName,
                        coachLastName,
                    })}
                />
            ) : (
                <CoachAvatarPlaceholder>
                    <CoachAvatarInitials>
                        {coachFirstName?.[0]}
                        {coachLastName?.[0]}
                    </CoachAvatarInitials>
                </CoachAvatarPlaceholder>
            )}
            <CoachNameWrapper>
                <CoachName>
                    {coachFirstName} {coachLastName}
                    {withInfo && (
                        <InfoButtonSpan
                            role="button"
                            onClick={onInfoClick}
                            data-testid={IntakeFormTestID.CoachInfoButton}
                        ></InfoButtonSpan>
                    )}
                </CoachName>
                <CoachCredentials>{coachCredentials}</CoachCredentials>
            </CoachNameWrapper>
        </Wrapper>
    );
};
